import React, { useState, useEffect, useRef } from 'react';
import { Maximize, Minimize } from 'lucide-react';
import { getEmbedUrl } from '../utils/videoEmbed';

// Embeds the film inline (YouTube/Vimeo/Drive) instead of linking out, with a
// semi-transparent, slowly-moving watermark identifying the reviewer burned
// over the player. This doesn't hide the underlying URL from a determined
// technical user (it's still in the iframe's src) — the point is that any
// screen recording or photo of a leak now carries who watched it and when,
// which is what actually deters/traces leaks, combined with the server-side
// view log (backend/src/db/reviewerViewLog.js).
export default function WatermarkedPlayer({ link, reviewer }) {
  const wrapperRef = useRef(null);
  const [pos, setPos] = useState({ top: 8, left: 6 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [stamp] = useState(() => new Date().toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  useEffect(() => {
    const move = () => setPos({ top: 8 + Math.random() * 78, left: 4 + Math.random() * 68 });
    const interval = setInterval(move, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Our own fullscreen control, fullscreening the WRAPPER (video + watermark
  // together) — not the iframe. YouTube's native fullscreen button only
  // fullscreens the iframe itself, which leaves the watermark behind (that
  // was the bug: watermark disappeared on fullscreen). We disable the
  // iframe's own fullscreen ability below so this button is the only path in.
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapperRef.current?.requestFullscreen();
  };

  if (!link) return null;
  const embed = getEmbedUrl(link);

  if (!embed) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer"
        style={{ display: 'inline-block', background: 'var(--pink)', color: '#fff', borderRadius: 'var(--r-md)', padding: '9px 18px', fontSize: 12, fontWeight: 800, textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        ▶ Watch Film
      </a>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: isFullscreen ? '100%' : undefined, aspectRatio: isFullscreen ? undefined : '16 / 9', borderRadius: isFullscreen ? 0 : 'var(--r-md)', overflow: 'hidden', background: '#000' }}>
      <iframe
        src={embed.embedUrl}
        title="Film"
        allow="autoplay; picture-in-picture"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
      <div style={{
        position: 'absolute', top: `${pos.top}%`, left: `${pos.left}%`,
        transition: 'top 1.5s ease, left 1.5s ease',
        pointerEvents: 'none', userSelect: 'none',
        color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700,
        textShadow: '0 1px 3px rgba(0,0,0,0.85)', whiteSpace: 'nowrap', letterSpacing: '0.02em',
      }}>
        {reviewer?.name} · {reviewer?.email} · {stamp}
      </div>
      <button onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        style={{ position: 'absolute', bottom: 10, right: 10, width: 32, height: 32, borderRadius: 8, background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {isFullscreen ? <Minimize size={15} /> : <Maximize size={15} />}
      </button>
    </div>
  );
}
